// src/services/addressService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import translateX from 'utils/translateX';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import { getCepProvider } from './cep-external/factory';
import { getGpsProvider } from './gps-external/factory';
import { getCpfProvider } from '../cpf-external/factory';
import { getCnpjProvider } from '../cnpj-external/factory';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const AVAILABLE_CEP_PROVIDERS = ['viacep', 'brasilapi'];
const AVAILABLE_GPS_PROVIDERS = ['jarvisly', 'nominatim', 'positionstack'];

// *******************************************************************************
// METHODS
// *******************************************************************************

/**
 * Converts universal POJO (from provider adapter) to application addressSchema format
 * This is the service layer - converts universal POJO to database schema
 * @param {Object} pojoCep - Universal POJO from provider adapter (apiViaCep, apiBrasilApi, etc)
 * @returns {Object} Address object matching application addressSchema
 *
 * Universal POJO structure (provider-agnostic):
 * {
 *   zip: string,
 *   source: string,
 *   street: string,
 *   complement: string,
 *   unit: string,
 *   neighborhood: string,
 *   city: string,
 *   state: string,
 *   stateName: string,
 *   region: string,
 *   areaCode: string,
 *   ibgeCode: string,
 *   giaCode: string,
 *   siafiCode: string,
 *   hasMultipleAddresses: boolean
 * }
 */
const decorateAddressFromPojo = pojoCep => {
  if (!pojoCep) {
    return null;
  }

  const {
    zip,
    source,
    street,
    complement,
    unit,
    neighborhood,
    city,
    state,
    stateName,
    region,
    areaCode,
    ibgeCode,
    giaCode,
    siafiCode,
    hasMultipleAddresses,
  } = pojoCep;

  return {
    zip: zip || '',
    addressTitle: translateX('main_address'),

    // Address fields (mapping universal POJO → addressSchema)
    address1: street || '',
    address2: complement || '',
    neighborhood: neighborhood || '',
    city: city || '',
    cityCode: ibgeCode || '', // IBGE code (7 digits) - required for NFe/NFCe cMun field
    region: state || '', // UF code (SP, RJ, etc)

    // Virtual field (computed on read)
    __city: city && state ? `${city} - ${state}` : '',

    // Additional info from API
    addressAdditionalInfo: {
      searchApi: source || '',
      note: complement || '',
      unit: unit || '',
      areaCode: areaCode || '',
      giaCode: giaCode || '',
      ibgeCode: ibgeCode || '',
      siafiCode: siafiCode || '',
      stateName: stateName || '', // Full state name (São Paulo, Rio de Janeiro, etc)
      regionName: region || '', // Region name (Sudeste, Sul, etc)
    },

    // Metadata about the CEP lookup
    addressMetadata: {
      countryCode: 'BR',
      foundByApi: true,
      apiUrl: getApiUrl(source, zip),
      zipWithMultipleAddress: hasMultipleAddresses || false,
      manual: false,
      apiFailure: false,
    },
  };
};

/**
 * Extracts address fields from database document
 * Returns only fields that match addressSchema structure
 * @param {Object} data - Database document (can contain extra fields)
 * @param {Object} [zipStateKey] - Optional ZIP lookup state object
 * @param {boolean} [zipStateKey.apiFailure] - If true, marks address as manual entry (API failed)
 * @param {boolean} [zipStateKey.addressFromApi] - Whether address came from API
 * @param {string} [zipStateKey.lastSearchFound] - Last searched ZIP code
 * @param {boolean} [zipStateKey.searching] - Whether currently searching
 * @param {string} [subdocumentField] - Optional flatten path to extract subdocument first (e.g., '__entity', 'moreAddress[0]')
 * @returns {Object|null} Clean address object with only addressSchema fields
 *
 * @example
 * // Extract from root level
 * const address = decorateAddressFromDatabase(entityDoc);
 *
 * @example
 * // Extract with zipStateKey (API failure = manual entry)
 * const address = decorateAddressFromDatabase(doc, { apiFailure: true });
 *
 * @example
 * // Extract from nested subdocument
 * const address = decorateAddressFromDatabase(doc, {}, 'moreAddress[0]');
 */
const decorateAddressFromDatabase = (data, zipStateKey = {}, subdocumentField = '') => {
  if (!data) {
    return null;
  }

  // If subdocumentField is provided, extract that subdocument first
  let sourceData = data;
  if (subdocumentField) {
    sourceData = getValueFromFlattenObj(data, subdocumentField);
    if (!sourceData) {
      return null;
    }
  }

  // Build addressMetadata with zipStateKey overrides
  let addressMetadata = null;
  if (sourceData.addressMetadata) {
    addressMetadata = {
      countryCode: sourceData.addressMetadata.countryCode || '',
      foundByApi: sourceData.addressMetadata.foundByApi || false,
      apiUrl: sourceData.addressMetadata.apiUrl || '',
      zipWithMultipleAddress: sourceData.addressMetadata.zipWithMultipleAddress || false,
      manual: sourceData.addressMetadata.manual || false,
      apiFailure: sourceData.addressMetadata.apiFailure || false,
    };

    // Override manual flag if API failed
    if (zipStateKey.apiFailure === true) {
      addressMetadata.manual = true;
      addressMetadata.apiFailure = true;
    }
  }

  // Extract only addressSchema fields
  return {
    // Basic address fields
    zip: sourceData.zip || '',
    address1: sourceData.address1 || '',
    address2: sourceData.address2 || '',
    number: sourceData.number || '',
    addressReference: sourceData.addressReference || '',
    addressTitle: sourceData.addressTitle || '',
    neighborhood: sourceData.neighborhood || '',
    city: sourceData.city || '',
    region: sourceData.region || '',

    // Coordinates array
    addressCoordinates: sourceData.addressCoordinates || [],

    // Nested objects (only if they exist)
    ...(sourceData.addressAdditionalInfo && {
      addressAdditionalInfo: {
        searchApi: sourceData.addressAdditionalInfo.searchApi || '',
        note: sourceData.addressAdditionalInfo.note || '',
        areaCode: sourceData.addressAdditionalInfo.areaCode || '',
        giaCode: sourceData.addressAdditionalInfo.giaCode || '',
        ibgeCode: sourceData.addressAdditionalInfo.ibgeCode || '',
        siafiCode: sourceData.addressAdditionalInfo.siafiCode || '',
      },
    }),

    // Apply addressMetadata with zipStateKey overrides
    ...(addressMetadata && { addressMetadata }),

    // Virtual fields (if present in data)
    ...(sourceData.__city && { __city: sourceData.__city }),
    ...(sourceData.__address && { __address: sourceData.__address }),
  };
};

/**
 * Main function to get Brazil CEP data
 * Uses Provider Factory Pattern with automatic fallback
 * Chain order: Provider 1 → Provider 2 → ...
 * @param {string} zip - CEP number
 * @param {string|Array<string>} [provider] - Provider(s) to use
 *   - If not specified: uses default chain (Provider 1 → Provider 2 → ...)
 *   - If string: single provider ('viacep' or 'brasilapi', or another)
 *   - If array: ordered provider names (['brasilapi', 'viacep'])
 * @returns {Promise<Object>} Result object with case and data
 *
 * Return structure:
 * {
 *   case: 1 | 2 | 3,
 *   data: Object | null,
 *   error: string | null
 * }
 *
 * Cases:
 * - CASE 1 (API failure): All providers are down
 *   { case: 1, data: null, error: 'All providers failed' }
 *
 * - CASE 2 (CEP not found): CEP is invalid or doesn't exist
 *   { case: 2, data: null, error: 'CEP não encontrado' }
 *
 * - CASE 3 (Success): CEP found and decorated
 *   { case: 3, data: { zip, address1, city, ... }, error: null }
 *
 * @example
 * // Use default chain (all providers)
 * const result = await getBrazilCep('13010111');
 *
 * @example
 * // Use only ViaCEP
 * const result = await getBrazilCep('13010111', 'viacep');
 *
 * @example
 * // Custom fallback order: BrasilAPI → ViaCEP (skip Next provider)
 * const result = await getBrazilCep('13010111', ['brasilapi', 'viacep']);
 */
const getBrazilCep = async (zip, provider = AVAILABLE_CEP_PROVIDERS) => {
  if (!zip) {
    console.error('[addressService] CEP is required');
    return { case: 1, data: null, error: 'CEP is required' };
  }

  const cleanZip = String(zip).replace(/\D/g, '');

  if (cleanZip.length !== 8) {
    console.error('[addressService] Invalid CEP length:', cleanZip);
    return { case: 2, data: null, error: 'Invalid CEP length' };
  }

  try {
    // Get provider with automatic fallback chain
    // Pass provider parameter to factory (string, array, or undefined)
    const { adapter } = getCepProvider({ provider });

    // Call searchAddress - factory handles fallback automatically
    const result = await adapter.searchAddress({ cep: cleanZip });

    // Result structure: { data, resolvedName, failedProviders }
    const pojo = result.data;

    // If CEP not found (invalid/doesn't exist) - CASE 2
    if (!pojo || !pojo.success) {
      appService.notification({
        flag: 'w',
        title: translateX('cep_not_found'),
        description: translateX('cep_not_found_description'),
        duration: 10,
      });

      return {
        case: 2,
        data: null,
        error: pojo?.error || 'CEP não encontrado',
      };
    }

    // CEP found successfully - convert POJO to addressSchema - CASE 3
    const decoratedAddress = decorateAddressFromPojo(pojo);

    if (decoratedAddress?.hasMultipleAddresses) {
      appService.notification({
        flag: 'i',
        title: translateX('unique_cep_title'),
        description: translateX('unique_cep_description'),
        duration: 0,
      });
    }

    return {
      case: 3,
      data: decoratedAddress,
      error: null,
    };
  } catch (error) {
    // All providers failed (timeout, connection error, etc.) - CASE 1
    // console.error('[addressService] All CEP providers failed:', error.message);

    appService.notification({
      key: 'cepApiErrorNotificationKey',
      flag: 'e',
      title: translateX('cep_service_unavailable'),
      description: translateX('cep_service_unavailable_description'),
      duration: 0,
    });

    return {
      case: 1,
      data: null,
      error: error.message || 'All providers failed',
    };
  }
};

/**
 * Main function to get GPS coordinates from address
 * Uses Provider Factory Pattern with automatic fallback
 * Chain order: Provider 1 → Provider 2 → ...
 * @param {Object} config - Address configuration
 * @param {string} config.address - Street address
 * @param {string} config.number - Street number
 * @param {string} config.city - City name
 * @param {string} config.region - State/region code (e.g., 'SP', 'RJ')
 * @param {string|Array<string>} [provider] - Provider(s) to use
 *   - If not specified: uses default chain (Provider 1 → Provider 2 → ...)
 *   - If string: single provider ('jarvisly', 'nominatim', 'positionstack')
 *   - If array: ordered provider names (['nominatim', 'jarvisly'])
 * @returns {Promise<Object>} Result object with case and data
 *
 * Return structure:
 * {
 *   case: 1 | 2 | 3,
 *   data: [lat, lng] | null,
 *   error: string | null
 * }
 *
 * Cases:
 * - CASE 1 (API failure): All providers are down
 *   { case: 1, data: null, error: 'All providers failed' }
 *
 * - CASE 2 (Address not found): Address doesn't exist or insufficient data
 *   { case: 2, data: null, error: 'Coordenadas não encontradas' }
 *
 * - CASE 3 (Success): Coordinates found
 *   { case: 3, data: [lat, lng], error: null }
 *
 * @example
 * // Use default chain (all providers)
 * const result = await getGpsCoordinates({ address: 'Rua ABC', number: '123', city: 'Campinas', region: 'SP' });
 *
 * @example
 * // Use only Jarvisly (backend)
 * const result = await getGpsCoordinates(config, 'jarvisly');
 *
 * @example
 * // Custom fallback order: Nominatim → Jarvisly (skip Positionstack)
 * const result = await getGpsCoordinates(config, ['nominatim', 'jarvisly']);
 */
const getGpsCoordinates = async (config, provider = AVAILABLE_GPS_PROVIDERS) => {
  if (!config || (!config.address && !config.city)) {
    console.error('[addressService] Address or city is required');
    return { case: 2, data: null, error: 'Insufficient address data' };
  }

  try {
    // Get provider with automatic fallback chain
    const { adapter } = getGpsProvider({ provider });

    // Call searchCoordinates - factory handles fallback automatically
    const result = await adapter.searchCoordinates(config);

    // Result structure: { data, resolvedName, failedProviders }
    const pojo = result.data;

    // If coordinates not found (invalid/insufficient data) - CASE 2
    if (!pojo || !pojo.success) {
      // appService.notification({
      //   flag: 'w',
      //   title: translateX('gps_not_found'),
      //   description: translateX('gps_not_found_description'),
      //   duration: 10,
      // });

      return {
        case: 2,
        data: null,
        error: pojo?.error || 'Coordenadas não encontradas',
      };
    }

    // Coordinates found successfully - CASE 3
    return {
      case: 3,
      data: pojo.coordinates, // [lat, lng]
      error: null,
    };
  } catch (error) {
    // All providers failed (timeout, connection error, etc.) - CASE 1
    console.error('[addressService] All GPS providers failed:', error.message);

    appService.notification({
      key: 'gpsApiErrorNotificationKey',
      flag: 'e',
      title: translateX('gps_service_unavailable'),
      description: translateX('gps_service_unavailable_description'),
      duration: 0,
    });

    return {
      case: 1,
      data: null,
      error: error.message || 'All providers failed',
    };
  }
};

/**
 * Main function to get CPF data from Receita Federal
 * Uses Provider Factory Pattern (currently single provider, expandable)
 * @param {string} cpf - CPF number (with or without formatting)
 * @param {string|Array<string>} [provider] - Provider(s) to use
 *   - If not specified: uses default provider (jarvisly)
 *   - If string: single provider name ('jarvisly')
 *   - If array: ordered provider names (for future expansion)
 * @returns {Promise<Object>} Result object with case and data
 *
 * Return structure:
 * {
 *   case: 1 | 2 | 3,
 *   data: Object | null,
 *   error: string | null
 * }
 *
 * Cases:
 * - CASE 1 (API failure): Provider is down
 *   { case: 1, data: null, error: 'Provider failed' }
 *
 * - CASE 2 (CPF not found): CPF is invalid or doesn't exist
 *   { case: 2, data: null, error: 'CPF não encontrado' }
 *
 * - CASE 3 (Success): CPF found and data returned
 *   { case: 3, data: { cpf, name, birthDate, ... }, error: null }
 *
 * @example
 * // Use default provider
 * const result = await getCpfData('12345678900');
 *
 * @example
 * // Explicitly specify provider
 * const result = await getCpfData('12345678900', 'jarvisly');
 */
const getCpfData = async (cpf, provider = 'jarvisly') => {
  if (!cpf) {
    console.error('[addressService] CPF is required');
    return { case: 1, data: null, error: 'CPF is required' };
  }

  const cleanCpf = String(cpf).replace(/\D/g, '');

  if (cleanCpf.length !== 11) {
    console.error('[addressService] Invalid CPF length:', cleanCpf);
    return { case: 2, data: null, error: 'Invalid CPF length' };
  }

  try {
    // Get provider (currently only jarvisly, but ready for expansion)
    const { adapter } = getCpfProvider({ provider });

    // Call searchCpf
    const result = await adapter.searchCpf({ cpf: cleanCpf });

    // Result structure: { data, resolvedName, failedProviders }
    const pojo = result.data;

    // If CPF not found (invalid/doesn't exist) - CASE 2
    if (!pojo || !pojo.success) {
      appService.notification({
        flag: 'w',
        title: translateX('cpf_not_found'),
        description: translateX('cpf_not_found_description'),
        duration: 10,
      });

      return {
        case: 2,
        data: null,
        error: pojo?.error || 'CPF não encontrado',
      };
    }

    // CPF found successfully - CASE 3
    return {
      case: 3,
      data: pojo,
      error: null,
    };
  } catch (error) {
    // Provider failed (timeout, connection error, etc.) - CASE 1
    console.error('[addressService] CPF provider failed:', error.message);

    appService.notification({
      key: 'cpfApiErrorNotificationKey',
      flag: 'e',
      title: translateX('cpf_service_unavailable'),
      description: translateX('cpf_service_unavailable_description'),
      duration: 0,
    });

    return {
      case: 1,
      data: null,
      error: error.message || 'Provider failed',
    };
  }
};

/**
 * Main function to get CNPJ data from database
 * Uses Provider Factory Pattern (currently single provider, expandable)
 * @param {string} cnpj - CNPJ number (with or without formatting)
 * @param {string|Array<string>} [provider] - Provider(s) to use
 *   - If not specified: uses default provider (jarvisly)
 *   - If string: single provider name ('jarvisly')
 *   - If array: ordered provider names (for future expansion)
 * @returns {Promise<Object>} Result object with case and data
 *
 * Return structure:
 * {
 *   case: 1 | 2 | 3,
 *   data: Object | null,
 *   error: string | null
 * }
 *
 * Cases:
 * - CASE 1 (API failure): Provider is down
 *   { case: 1, data: null, error: 'Provider failed' }
 *
 * - CASE 2 (CNPJ not found): CNPJ is invalid or doesn't exist
 *   { case: 2, data: null, error: 'CNPJ não encontrado' }
 *
 * - CASE 3 (Success): CNPJ found and data returned
 *   { case: 3, data: { cnpj, name, fullName, ... }, error: null }
 *
 * @example
 * // Use default provider
 * const result = await getCnpjData('12345678000190');
 *
 * @example
 * // Explicitly specify provider
 * const result = await getCnpjData('12345678000190', 'jarvisly');
 */
const getCnpjData = async (cnpj, provider = 'jarvisly') => {
  if (!cnpj) {
    console.error('[addressService] CNPJ is required');
    return { case: 1, data: null, error: 'CNPJ is required' };
  }

  const cleanCnpj = String(cnpj).replace(/\D/g, '');

  if (cleanCnpj.length !== 14) {
    console.error('[addressService] Invalid CNPJ length:', cleanCnpj);
    return { case: 2, data: null, error: 'Invalid CNPJ length' };
  }

  try {
    // Get provider (currently only jarvisly, but ready for expansion)
    const { adapter } = getCnpjProvider({ provider });

    // Call searchCnpj
    const result = await adapter.searchCnpj({ cnpj: cleanCnpj });

    // Result structure: { data, resolvedName, failedProviders }
    const pojo = result.data;

    // If CNPJ not found (invalid/doesn't exist) - CASE 2
    if (!pojo || !pojo.success) {
      appService.notification({
        flag: 'w',
        title: translateX('cnpj_not_found'),
        description: translateX('cnpj_not_found_description'),
        duration: 10,
      });

      return {
        case: 2,
        data: null,
        error: pojo?.error || 'CNPJ não encontrado',
      };
    }

    // CNPJ found successfully - CASE 3
    return {
      case: 3,
      data: pojo,
      error: null,
    };
  } catch (error) {
    // Provider failed (timeout, connection error, etc.) - CASE 1
    console.error('[addressService] CNPJ provider failed:', error.message);

    appService.notification({
      key: 'cnpjApiErrorNotificationKey',
      flag: 'e',
      title: translateX('cnpj_service_unavailable'),
      description: translateX('cnpj_service_unavailable_description'),
      duration: 0,
    });

    return {
      case: 1,
      data: null,
      error: error.message || 'Provider failed',
    };
  }
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  getBrazilCep,
  getGpsCoordinates,
  getCpfData,
  getCnpjData,
  decorateAddressFromPojo,
  decorateAddressFromDatabase,
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Gets the API URL based on the source provider
 * Uses centralized API constants from constants/Api.js
 * @param {string} source - Provider source name
 * @param {string} zip - CEP number
 * @returns {string} API URL
 */
function getApiUrl(source, zip) {
  const cleanZip = String(zip).replace(/\D/g, '');
  const formattedZip = `${cleanZip.slice(0, 5)}-${cleanZip.slice(5)}`;

  switch (source) {
    case 'brasilapi':
      return mergeUrlParamsQuery({
        url: API.PROVIDERS.CEP.BRASILAPI,
        params: { cep: cleanZip },
      });
    case 'viacep':
      return mergeUrlParamsQuery({
        url: API.PROVIDERS.CEP.VIACEP,
        params: { cep: cleanZip },
      });
    default:
      return '';
  }
}
